import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import { capitalize } from '@/helpers/formatting';
import { hasIntlDisplayNames } from '@/helpers/intl';
import i18n from '@/i18n';
import { useCompanyStore } from './company';
import { useUserStore } from './user';

const locales = config.locales;
type Locale = (typeof locales)[number];

const isLocale = (val: string): val is Locale =>
  (locales as readonly string[]).includes(val);

const sanitizeLocale = (val: string): Locale =>
  isLocale(val) ? val : locales[0];

export const useLocalizationStore = defineStore('localization', () => {
  const languageLabels: Record<Locale, string> = (
    locales as readonly Locale[]
  ).reduce(
    (acc, locale) => {
      if (!hasIntlDisplayNames()) {
        const { t } = useI18n();
        acc[locale] = t(`global.languages.${locale}`);
        return acc;
      }

      const displayNames = new Intl.DisplayNames([locale], {
        type: 'language',
      });
      const label = displayNames.of(locale) ?? locale;
      acc[locale] = capitalize(label);
      return acc;
    },
    {} as Record<Locale, string>,
  );

  const companyLanguage = computed<Locale>(() => {
    const { company } = useCompanyStore();
    return sanitizeLocale((company as any).language);
  });

  const userLanguage = computed<Locale | null>(() => {
    const { user } = useUserStore();
    const raw = user?.settings?.custom?.language?.toLowerCase() ?? null;
    return raw && isLocale(raw) ? raw : null;
  });

  const chosenLanguage = computed<Locale>(
    () => userLanguage.value || companyLanguage.value,
  );

  watch(chosenLanguage, (val) => {
    if (!val) return; // When there is no user or company language, don't set the i18n locale

    const { isTreatwellUser } = useCompanyStore();
    if (isTreatwellUser) {
      val += '-tw';
    }

    i18n.locale.value = val;
  });

  const language = computed<Locale>(() => chosenLanguage.value || 'en');

  return {
    language,
    languageLabels,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useLocalizationStore, import.meta.hot),
  );
}
